// Enterprise license management component (master email only)

import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw, Copy, Check, Eye, EyeOff, ChevronUp } from 'lucide-react';
import { api } from '../services/api';
import type { EnterpriseLicense } from '../types';

function maskKey(key: string): string {
  if (key.length <= 12) return key;
  return key.slice(0, 12) + '\u2022'.repeat(16);
}

function LicenseKeyDisplay({ licenseKey }: { licenseKey: string }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyKey = async () => {
    await navigator.clipboard.writeText(licenseKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center gap-2 mt-2 bg-secondary border border-border rounded-md px-3 py-2">
      <input
        type="text"
        readOnly
        value={revealed ? licenseKey : maskKey(licenseKey)}
        className="flex-1 min-w-0 bg-transparent text-xs font-mono text-foreground border-none outline-none cursor-default truncate"
        onClick={(e) => { if (revealed) (e.target as HTMLInputElement).select(); }}
      />
      <button
        onClick={() => setRevealed(!revealed)}
        className="text-muted-foreground hover:text-foreground transition-colors p-1 shrink-0"
        title={revealed ? 'Hide' : 'Reveal'}
      >
        {revealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
      </button>
      <button
        onClick={copyKey}
        className="text-muted-foreground hover:text-foreground transition-colors p-1 shrink-0"
        title="Copy license key"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

export function EnterpriseManager() {
  const [licenses, setLicenses] = useState<EnterpriseLicense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);

  // Renew state
  const [renewId, setRenewId] = useState<string | null>(null);
  const [renewDays, setRenewDays] = useState(365);
  const [isRenewing, setIsRenewing] = useState(false);
  const [renewedKey, setRenewedKey] = useState<string | null>(null);

  // Form state
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [durationDays, setDurationDays] = useState(365);

  useEffect(() => {
    loadLicenses();
  }, []);

  const loadLicenses = async () => {
    try {
      const result = await api.listEnterpriseLicenses();
      setLicenses(result.licenses);
    } catch {
      // silently fail
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsGenerating(true);
    setGeneratedKey(null);

    try {
      const result = await api.generateEnterpriseLicense({
        customerId: customerId.trim(),
        customerName: customerName.trim(),
        durationDays,
        features: ['all'],
        maxAssistants: -1,
        maxMcpServers: -1,
        maxKnowledgeBases: -1,
        maxSessionsPerMonth: -1,
      });
      setGeneratedKey(result.licenseKey);
      setCustomerId('');
      setCustomerName('');
      setDurationDays(365);
      await loadLicenses();
    } catch {
      alert('Failed to generate enterprise license.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRenew = async () => {
    if (!renewId) return;
    setIsRenewing(true);
    setRenewedKey(null);
    try {
      const result = await api.renewEnterpriseLicense(renewId, renewDays);
      setRenewedKey(result.licenseKey);
      await loadLicenses();
    } catch {
      alert('Failed to renew license.');
    } finally {
      setIsRenewing(false);
    }
  };

  const activeLicenses = licenses.filter(l => !l.isExpired);
  const expiredLicenses = licenses.filter(l => l.isExpired);

  return (
    <div className="mt-12">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-medium text-accent uppercase tracking-wider">Enterprise Licenses</p>
          <p className="text-sm text-muted-foreground mt-1">Generate and manage enterprise license keys</p>
        </div>
        <button
          onClick={() => { setShowForm(!showForm); setGeneratedKey(null); }}
          className="inline-flex items-center gap-2 bg-accent text-white font-medium px-4 py-2 rounded-md hover:bg-accent/90 transition-all text-sm"
        >
          {showForm ? <ChevronUp className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showForm ? 'Close' : 'Generate License'}
        </button>
      </div>

      {/* Generate form */}
      {showForm && (
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          {generatedKey ? (
            <div>
              <p className="text-sm font-medium text-foreground mb-3">License generated successfully. You can always copy this key again from the list below.</p>
              <LicenseKeyDisplay licenseKey={generatedKey} />
            </div>
          ) : (
            <form onSubmit={handleGenerate} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">Customer ID</label>
                  <input
                    type="text"
                    required
                    value={customerId}
                    onChange={(e) => setCustomerId(e.target.value)}
                    placeholder="acme-corp"
                    className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring/50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">Customer Name</label>
                  <input
                    type="text"
                    required
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="ACME Corporation"
                    className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring/50"
                  />
                </div>
              </div>
              <div className="max-w-xs">
                <label className="block text-sm font-medium text-muted-foreground mb-1">Duration (days)</label>
                <input
                  type="number"
                  required
                  min={1}
                  max={3650}
                  value={durationDays}
                  onChange={(e) => setDurationDays(parseInt(e.target.value) || 365)}
                  className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50"
                />
              </div>
              <button
                type="submit"
                disabled={isGenerating}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-medium px-4 py-2 rounded-md hover:bg-primary/90 transition-all text-sm disabled:opacity-50"
              >
                {isGenerating ? 'Generating...' : 'Generate'}
              </button>
            </form>
          )}
        </div>
      )}

      {/* Renew modal */}
      {renewId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-foreground mb-2">Renew License</h3>
            <p className="text-sm text-muted-foreground mb-4">
              This generates a new license key with the same customer details and a fresh expiry. The old key will continue to work until its original expiry date.
            </p>
            {renewedKey ? (
              <div>
                <p className="text-sm font-medium text-foreground mb-2">New license key:</p>
                <LicenseKeyDisplay licenseKey={renewedKey} />
                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => { setRenewId(null); setRenewedKey(null); setRenewDays(365); }}
                    className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-muted-foreground mb-1">Duration (days)</label>
                  <input
                    type="number"
                    min={1}
                    max={3650}
                    value={renewDays}
                    onChange={(e) => setRenewDays(parseInt(e.target.value) || 365)}
                    className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => { setRenewId(null); setRenewDays(365); }}
                    disabled={isRenewing}
                    className="px-4 py-2 text-sm border border-border rounded-md text-foreground hover:bg-secondary transition-colors bg-transparent"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRenew}
                    disabled={isRenewing}
                    className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent/90 transition-colors disabled:opacity-50"
                  >
                    {isRenewing ? 'Renewing...' : 'Renew'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Active licenses list */}
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : activeLicenses.length === 0 ? (
        <p className="text-sm text-muted-foreground">No active enterprise licenses.</p>
      ) : (
        <div className="space-y-3">
          {activeLicenses.map(lic => (
            <div key={lic.id} className="bg-card border border-border rounded-xl p-4 hover:border-accent/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground truncate">{lic.customerName}</span>
                    <span className="text-xs text-muted-foreground font-mono">{lic.customerId}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {lic.expiresAt && <>Expires: {new Date(lic.expiresAt).toLocaleDateString()}</>}
                    {' \u00b7 '}
                    Created: {new Date(lic.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <button
                  onClick={() => setRenewId(lic.id)}
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-accent transition-colors px-3 py-1.5 rounded-md hover:bg-secondary shrink-0"
                  title="Renew license"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Renew
                </button>
              </div>
              {lic.licenseKey && <LicenseKeyDisplay licenseKey={lic.licenseKey} />}
            </div>
          ))}
        </div>
      )}

      {/* Expired licenses */}
      {expiredLicenses.length > 0 && (
        <details className="mt-4">
          <summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
            {expiredLicenses.length} expired license{expiredLicenses.length !== 1 ? 's' : ''}
          </summary>
          <div className="space-y-3 mt-2">
            {expiredLicenses.map(lic => (
              <div key={lic.id} className="bg-card border border-border rounded-xl p-4 opacity-50">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate line-through">{lic.customerName}</span>
                      <span className="text-xs text-muted-foreground font-mono">{lic.customerId}</span>
                      <span className="text-xs bg-red-500/20 text-red-400 border border-red-500/30 px-2 py-0.5 rounded">expired</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Expired: {lic.expiresAt && new Date(lic.expiresAt).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={() => setRenewId(lic.id)}
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-accent transition-colors px-3 py-1.5 rounded-md hover:bg-secondary shrink-0"
                    title="Renew license"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Renew
                  </button>
                </div>
                {lic.licenseKey && <LicenseKeyDisplay licenseKey={lic.licenseKey} />}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
